/**
 * Player-related constants for homepage and player pages.
 * Includes top players, personalisation categories, and hero shirt benefits.
 *
 * Types are defined inline — this file is self-contained.
 */

// ─── Inline Types ───────────────────────────────────────────────────────────

export interface Player {
  id: string;
  name: string;
  team: string;
  image?: string;
  /** Optional category for filtering (e.g. 'current-heroes', 'legends') */
  category?: string;
  productCount?: number;
}

export interface PersonalisationCategory {
  id: string;
  label: string;
  image: string;
}

export interface PersonalizationBenefit {
  id: string;
  title: string;
  description: string;
}

// ─── Top Players ────────────────────────────────────────────────────────────

export const TOP_PLAYERS: Player[] = [
  { id: 'cristiano-ronaldo', name: 'Cristiano Ronaldo', team: 'Al Nassr', image: '/images/homepage/shop-by-player/cristiano-ronaldo.jpg' },
  { id: 'lionel-messi', name: 'Lionel Messi', team: 'Inter Miami', image: '/images/homepage/shop-by-player/lionel-messi.jpg' },
  { id: 'lamine-yamal', name: 'Lamine Yamal', team: 'Barcelona', image: '/images/homepage/shop-by-player/lamine-yamal.jpg' },
  { id: 'erling-haaland', name: 'Erling Haaland', team: 'Manchester City', image: '/images/homepage/shop-by-player/erling-haaland.jpg' },
  { id: 'bukayo-saka', name: 'Bukayo Saka', team: 'Arsenal', image: '/images/homepage/shop-by-player/bukayo-saka.jpg' },
  { id: 'vinicius-jr', name: 'Vinicius Jr', team: 'Real Madrid', image: '/images/homepage/shop-by-player/vinicius-jr.jpg' },
  { id: 'harry-kane', name: 'Harry Kane', team: 'Bayern Munich', image: '/images/homepage/shop-by-player/harry-kane.jpg' },
  { id: 'jude-bellingham', name: 'Jude Bellingham', team: 'Real Madrid', image: '/images/homepage/shop-by-player/jude-bellingham.jpg' },
  { id: 'florian-wirtz', name: 'Florian Wirtz', team: 'Liverpool', image: '/images/homepage/shop-by-player/florian-wirtz.jpg' },
  { id: 'cole-palmer', name: 'Cole Palmer', team: 'Chelsea', image: '/images/homepage/shop-by-player/cole-palmer.jpg' },
];

/** Search query used to route each "Shop by player" card to real matching products —
 * there's no dedicated per-player landing page yet, so this reuses the site's search. */
export const PLAYER_SEARCH_QUERY: Record<string, string> = {
  'cristiano-ronaldo': 'Cristiano Ronaldo',
  'lionel-messi': 'Lionel Messi',
  'lamine-yamal': 'Lamine Yamal',
  'erling-haaland': 'Erling Haaland',
  'bukayo-saka': 'Bukayo Saka',
  'vinicius-jr': 'Vinicius',
  'harry-kane': 'Harry Kane',
  'jude-bellingham': 'Jude Bellingham',
  'florian-wirtz': 'Florian Wirtz',
  'cole-palmer': 'Cole Palmer',
};

// ─── Personalisation Categories ─────────────────────────────────────────────

export const PERSONALISATION_CATEGORIES: PersonalisationCategory[] = [
  { id: 'hero-shirts', label: 'Hero Shirts', image: '/images/Hero Shirts Thumbnail.png' },
  { id: 'custom-names', label: 'Custom Names', image: '/images/Custom Names Thumbnail.png' },
  { id: 'custom-tshirts', label: 'Custom T-Shirts', image: '/images/Custom T-Shirts Thumbnail.png' },
  { id: 'existing-kit', label: 'Existing Kit Printing', image: '/images/Existing Kit Printing Thumbnail.png' },
];

// ─── Hero Shirts Benefits ───────────────────────────────────────────────────

export const HERO_SHIRTS_BENEFITS: PersonalizationBenefit[] = [
  { id: 'personal', title: 'Make it personal', description: 'Customise any hero shirt with any name or number for you or as an amazing gift.' },
  { id: 'range', title: 'Huge Range of Hero Shirts', description: 'Choose from thousands of club and national team shirts.' },
  { id: 'players', title: 'Favourite Players Past & Present', description: 'Get legendary names or today\'s stars printed on your shirt.' },
];
